import { useEffect, useRef, useCallback, useState } from 'react';
import { invokeFunctionWithAuth } from '@/lib/supabase/functions';
import { toast } from 'sonner';

export interface LocationUpdate {
  lat: number;
  lng: number;
  heading?: number;
  speed?: number;
  accuracy?: number;
  timestamp: string;
}

interface UseFilmmakerLocationOptions {
  bookingId: string;
  filmmakerId: string;
  enabled: boolean; // Only track when status is en_route
  updateInterval?: number; // Milliseconds between updates (default: 15000 = 15 seconds)
  minDistanceChange?: number; // Minimum distance in meters to trigger update (default: 50m)
  onLocationUpdate?: (location: LocationUpdate) => void;
  onError?: (error: GeolocationPositionError) => void;
}

interface UseFilmmakerLocationReturn {
  currentLocation: LocationUpdate | null;
  isTracking: boolean;
  error: GeolocationPositionError | null;
  startTracking: () => void;
  stopTracking: () => void;
}

/**
 * Hook to track filmmaker location using Geolocation API
 * Updates location to Supabase via edge function
 */
export function useFilmmakerLocation({
  bookingId,
  filmmakerId,
  enabled,
  updateInterval = 15000, // 15 seconds default
  minDistanceChange = 50, // 50 meters default
  onLocationUpdate,
  onError,
}: UseFilmmakerLocationOptions): UseFilmmakerLocationReturn {
  const [currentLocation, setCurrentLocation] = useState<LocationUpdate | null>(null);
  const [isTracking, setIsTracking] = useState(false);
  const [error, setError] = useState<GeolocationPositionError | null>(null);
  
  const watchIdRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<{ lat: number; lng: number; timestamp: number } | null>(null);
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Calculate distance between two coordinates (Haversine formula)
  const calculateDistance = useCallback((lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  }, []);

  // Update location to Supabase
  const updateLocationToServer = useCallback(async (position: GeolocationPosition) => {
    const locationUpdate: LocationUpdate = {
      lat: position.coords.latitude,
      lng: position.coords.longitude,
      heading: position.coords.heading ?? undefined,
      speed: position.coords.speed ?? undefined,
      accuracy: position.coords.accuracy ?? undefined,
      timestamp: new Date().toISOString(),
    };

    // Check if location changed significantly
    const lastUpdate = lastUpdateRef.current;
    if (lastUpdate) {
      const distance = calculateDistance(
        lastUpdate.lat,
        lastUpdate.lng,
        locationUpdate.lat,
        locationUpdate.lng
      );
      
      // Only update if distance changed significantly or enough time has passed
      const timeSinceLastUpdate = Date.now() - lastUpdate.timestamp;
      if (distance < minDistanceChange && timeSinceLastUpdate < updateInterval) {
        // Location hasn't changed enough, skip update
        return;
      }
    }

    try {
      const { error: updateError } = await invokeFunctionWithAuth('update-filmmaker-location', {
        bookingId,
        filmmakerId,
        lat: locationUpdate.lat,
        lng: locationUpdate.lng,
        heading: locationUpdate.heading,
        speed: locationUpdate.speed,
        accuracy: locationUpdate.accuracy,
      });

      if (updateError) {
        console.error('Error updating location to server:', updateError);
        // Don't show toast for every error to avoid spam
        if (updateError.status === 403 || updateError.status === 401) {
          toast.error('Não autorizado para atualizar localização');
          stopTracking();
        }
        return;
      }

      // Update local state
      setCurrentLocation(locationUpdate);
      lastUpdateRef.current = {
        lat: locationUpdate.lat,
        lng: locationUpdate.lng,
        timestamp: Date.now(),
      };

      // Call callback if provided
      if (onLocationUpdate) {
        onLocationUpdate(locationUpdate);
      }
    } catch (err) {
      console.error('Error in updateLocationToServer:', err);
    }
  }, [bookingId, filmmakerId, minDistanceChange, updateInterval, calculateDistance, onLocationUpdate]);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
      updateTimeoutRef.current = null;
    }
    setIsTracking(false);
  }, []);

  const startTracking = useCallback(() => {
    if (!enabled || !bookingId || !filmmakerId) {
      return;
    }

    if (!navigator.geolocation) {
      const error = {
        code: 0,
        message: 'Geolocation is not supported by this browser',
        PERMISSION_DENIED: 1,
        POSITION_UNAVAILABLE: 2,
        TIMEOUT: 3,
      } as GeolocationPositionError;
      setError(error);
      if (onError) {
        onError(error);
      }
      return;
    }

    setIsTracking(true);
    setError(null);

    const options: PositionOptions = {
      enableHighAccuracy: false, // Balance between accuracy and battery
      timeout: 30000, // 30 seconds timeout (increased for better reliability)
      maximumAge: 60000, // Accept cached position up to 60 seconds old
    };

    const handlePosition = (position: GeolocationPosition) => {
      updateLocationToServer(position);
    };

    const handleError = (err: GeolocationPositionError) => {
      console.error('Geolocation error:', err);
      setError(err);
      if (onError) {
        onError(err);
      }
      
      // Show user-friendly error messages (only once to avoid spam)
      // Don't show toast for timeout errors repeatedly - they're common
      switch (err.code) {
        case err.PERMISSION_DENIED:
          toast.error('Permissão de localização negada. Por favor, permita o acesso à localização nas configurações do navegador.');
          stopTracking();
          break;
        case err.POSITION_UNAVAILABLE:
          // Only show once, not on every retry
          if (!error || error.code !== err.POSITION_UNAVAILABLE) {
            toast.error('Localização indisponível. Verifique se o GPS está ativado.');
          }
          break;
        case err.TIMEOUT:
          // Timeout is common, especially indoors or in development
          // Don't show toast for timeout - just log it
          console.warn('Geolocation timeout - will retry automatically');
          // Don't stop tracking on timeout - watchPosition will keep trying
          break;
        default:
          if (!error || error.code !== err.code) {
            toast.error('Erro ao obter localização. Tentando novamente...');
          }
          break;
      }
    };

    // Start watching position (continuous updates)
    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePosition,
      handleError,
      options
    );

    // Also try to get current position immediately (helps with first location)
    navigator.geolocation.getCurrentPosition(
      handlePosition,
      (err) => {
        // Ignore errors on getCurrentPosition - watchPosition will handle it
        // This is just a best-effort attempt to get location faster
        console.log('getCurrentPosition failed (watchPosition will continue):', err.message);
      },
      options
    );
  }, [enabled, bookingId, filmmakerId, updateLocationToServer, onError, stopTracking, error]);

  // Auto-start/stop tracking based on enabled prop
  useEffect(() => {
    if (enabled && bookingId && filmmakerId) {
      startTracking();
    } else {
      stopTracking();
    }

    return () => {
      stopTracking();
    };
  }, [enabled, bookingId, filmmakerId, startTracking, stopTracking]);

  return {
    currentLocation,
    isTracking,
    error,
    startTracking,
    stopTracking,
  };
}

